import { PATHS } from 'src/constants'

import { join, stripEnding } from './url-path.util'

export function isProductHomePath(
  path: string,
  simpleProductId?: string | null,
): boolean {
  if (!simpleProductId) return false

  const clean = path.split('?')[0].split('#')[0]
  const segments = clean.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean)

  // language-prefixed (e.g., /en/:simpleId) or plain (/:simpleId)
  const maybeLang = segments[0]
  const isLang = /^[a-z]{2}$/i.test(maybeLang) // keep in sync with your routing

  if (!isLang && segments.length === 1) {
    return segments[0] === simpleProductId
  }
  if (isLang && segments.length === 2) {
    return segments[1] === simpleProductId
  }
  return false
}

export function stripProductHomeSegment(path: string): string {
  if (!path) return path

  const clean = path.replace(/\/+$/, '')
  if (clean.endsWith('/home')) {
    const withoutHome = clean.slice(0, -5)
    return withoutHome.length ? withoutHome : '/'
  }
  return clean.length ? clean : '/'
}

const PRODUCT_DEEP_LINK_SEGMENTS = [PATHS.JOURNEY, PATHS.IMPACTS, PATHS.HOME] as const

export type ProductNavPaths = {
  basePath: string
  productPath: string
  journeyPath: string
  impactsPath: string
}

export function buildProductNavPaths(pathname: string): ProductNavPaths {
  const basePath = PRODUCT_DEEP_LINK_SEGMENTS.reduce(
    (current, segment) => stripEnding(current, segment),
    pathname || '/',
  )

  return {
    basePath,
    productPath: join(basePath, PATHS.HOME),
    journeyPath: join(basePath, PATHS.JOURNEY),
    impactsPath: join(basePath, PATHS.IMPACTS),
  }
}

const PRODUCT_PAGE_SEGMENTS = new Set<string>([PATHS.HOME, PATHS.JOURNEY, PATHS.IMPACTS])

export function ensureProductHomePath(path?: string | null): string | undefined {
  if (!path || typeof path !== 'string') return undefined

  const trimmed = path.trim()
  if (!trimmed) return undefined

  const pathMatch = trimmed.match(/^([^?#]*)(.*)$/)
  const rawPath = pathMatch?.[1] ?? ''
  const suffix = pathMatch?.[2] ?? ''

  const normalized = rawPath.startsWith('/') ? rawPath : `/${rawPath}`
  const withoutTrailingSlash = normalized.replace(/\/+$/, '')
  const segments = withoutTrailingSlash.split('/').filter(Boolean)

  if (!segments.length) {
    const base = withoutTrailingSlash || '/'
    return base === '/' ? `/${PATHS.HOME}${suffix}` : `${base}/${PATHS.HOME}${suffix}`
  }

  const lastSegment = segments[segments.length - 1]
  if (PRODUCT_PAGE_SEGMENTS.has(lastSegment)) {
    return `${withoutTrailingSlash}${suffix}`
  }

  return `${withoutTrailingSlash}/${PATHS.HOME}${suffix}`
}
